import {
  pixelBufferType,
  textureMagFilter,
  textureMinFilter,
  textureWrapper,
  RBIntFormat,
  memoryType
} from "_src/engine/constants";
import glTypes from "_src/engine/gl_types";
import GL from "_src/engine/gl";
import extensions from "_src/engine/extensions";
import { isPowerOfTwo } from "_src/engine/math";
import memoryProfiler from "_src/engine/memory_profiler";
import output from "_src/engine/output";

let _textureCounter = 0;

export default {

  create(type, params = {}) {

    const gl = GL.getContext();

    switch (type) {
      case pixelBufferType.T_RGBA:
        return new ColorRGBATexture(gl, params);

      case pixelBufferType.T_RGBA16F:
        return new ColorFloatRenderingTexture(gl, params);

      case pixelBufferType.RB_RGBA:
        params.format = params.format ? params.format : RBIntFormat.R5G6B5;
        return new Renderbuffer(gl, params);

      case pixelBufferType.T_DEPTH:
        return new DepthStencilTexture(gl, params);

      case pixelBufferType.RB_DEPTH:
        params.format = params.format ? params.format : RBIntFormat.D16U;
        return new Renderbuffer(gl, params);

      case pixelBufferType.RB_STENCIL:
        params.format = params.format ? params.format : RBIntFormat.S8;
        return new Renderbuffer(gl, params);

      case pixelBufferType.T_RG32F:
        return new ColorRG32FTexture(gl, params);

      case pixelBufferType.T_COMPRESSED:
        return new ColorRGBACompressedTexture(gl, params);

      case pixelBufferType.CM_RGBA:
        return new CubeMap(gl, params);
    }

  }

}

class Texture {
  constructor(gl, params) {
    this.isTexture = true;
    this.id = _textureCounter++;

    const {width, height} = fixTextureSize(params.width || 1, params.height || 1);

    this.width = width;
    this.height = height;
    this.flipY = params.flipY !== undefined ? params.flipY : false;
    this.immutable = params.immutable !== undefined ? params.immutable : false;
    this.levels = params.levels !== undefined ? params.levels : 1;
    this.internalFormat = 0;
    this.dataFormat = 0;
    this.dataType = 0;
    this.target = gl.TEXTURE_2D;

    this._initFormats(gl, params);

    this.pixelBuffer = gl.createTexture();
    gl.bindTexture(this.target, this.pixelBuffer);

    this._setupStates(gl, params);

    const wrapper = params.wrapper !== undefined ? params.wrapper : textureWrapper.CLAMP;
    this.setWrapBehavior(gl, wrapper);

    this._preInitStorage(gl);

    gl.bindTexture(this.target, null);

    memoryProfiler.markMemoryAllocated(this.pixelBuffer, memoryType.GPU_TEXTURES, width * height * 4);
  }

  //for override
  _initFormats() {}
  _setupStates() {}

  _preInitStorage(gl) {
    if (this.immutable && gl.texStorage2D) {
      gl.texStorage2D(this.target, this.levels, this.internalFormat, this.width, this.height);
    } else {
      for (let level=0; level<this.levels; level++) {
        gl.texImage2D(this.target, level, this.internalFormat, this.width / Math.pow(2, level), this.height / Math.pow(2, level), 0, this.dataFormat, this.dataType, null);
      }
    }
  }

  setMagFilter(gl, filter) {
    const magFilter = glTypes.getMagFilter(filter);
    gl.texParameteri(this.target, gl.TEXTURE_MAG_FILTER, magFilter);
  }

  setMinFilter(gl, filter) {
    const minFilter = glTypes.getMinFilter(filter);
    gl.texParameteri(this.target, gl.TEXTURE_MIN_FILTER, minFilter);
  }

  setWrapBehavior(gl, wrapper) {
    const textWrapper = glTypes.getWrapBehavior(wrapper);
    gl.texParameteri(this.target, gl.TEXTURE_WRAP_S, textWrapper);
    gl.texParameteri(this.target, gl.TEXTURE_WRAP_T, textWrapper);
  }

  initMipMapLevelImage(level, image, target = null) {
    const gl = GL.getContext();
    const imageTarget = target === null ? this.target : glTypes.getCubeMapTarget(target);

    if (image.width !== this.width / Math.pow(2, level) || image.height !== this.height / Math.pow(2, level)) {
      output.error(output.DEVELOPERS_ERROR, "Can't initialize MipMap level. The image doesn't match the texture.");
      return;
    }

    gl.bindTexture(this.target, this.pixelBuffer);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, this.flipY);

    if (GL.checkWebGL2()) {
      gl.texSubImage2D(imageTarget, level, 0, 0, image.width, image.height, this.dataFormat, this.dataType, image.getImage());
    } else {
      gl.texSubImage2D(imageTarget, level, 0, 0, this.dataFormat, this.dataType, image.getImage());
    }

    gl.bindTexture(this.target, null);

    memoryProfiler.markMemoryAllocated(this.pixelBuffer, memoryType.GPU_TEXTURES, image.width * image.height * 4);
  }

  initMipMapLevelBuffer(level, buffer) {

    const gl = GL.getContext();

    gl.bindTexture(this.target, this.pixelBuffer);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, this.flipY);
    gl.texSubImage2D(this.target, level, 0, 0, this.width, this.height, this.dataFormat, this.dataType, buffer);
    gl.bindTexture(this.target, null);

  }

  generateMipmap() {

    if (!GL.checkWebGL2() && !this.isPowerOfTwo()) {
      return;
    }

    const gl = GL.getContext();

    gl.bindTexture(this.target, this.pixelBuffer);
    gl.generateMipmap(this.target);
    gl.bindTexture(this.target, null);

  }

  isPowerOfTwo() {
    return isPowerOfTwo(this.width) && isPowerOfTwo(this.height);
  }

  resize(w, h) {

    if (this.immutable) {
      output.error(output.DEVELOPERS_ERROR, "Can't resize immutable texture");
      return;
    }

    const {width, height} = fixTextureSize(w, h);

    const gl = GL.getContext();

    gl.bindTexture(this.target, this.pixelBuffer);
    gl.texImage2D(this.target, 0, this.internalFormat, width, height, 0, this.dataFormat, this.dataType, null);
    gl.bindTexture(this.target, null);

    this.width = width;
    this.height = height;

    memoryProfiler.markMemoryAllocated(this.pixelBuffer, memoryType.GPU_TEXTURES, this.width * this.height * 4);

  }

  cleanup() {
    const gl = GL.getContext();
    gl.deleteTexture(this.pixelBuffer);
    memoryProfiler.markMemoryReleased(this.pixelBuffer);
  }

}

class ColorRGBATexture extends Texture {
  constructor(gl, params) {
    super(gl, params);
  }

  _initFormats(gl) {
    if (this.immutable && gl.texStorage2D) {
      this.internalFormat = gl.RGBA8;
    } else {
      this.internalFormat = gl.RGBA;
    }
    this.dataType = gl.UNSIGNED_BYTE;
    this.dataFormat = gl.RGBA;
  }

  _setupStates(gl, params) {
    const anisotropy = params.anisotropy !== undefined ? params.anisotropy : 0;
    if (anisotropy > 0) {
      const ext = extensions.get("EXT_texture_filter_anisotropic");

      if (ext) {
        const aniso = Math.min(anisotropy, gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT));
        gl.texParameterf(this.target, ext.TEXTURE_MAX_ANISOTROPY_EXT, aniso);
      }
    }

    const magFilter = params.magFilter !== undefined ? params.magFilter : textureMagFilter.LINEAR;
    const minFilter = params.minFilter !== undefined ? params.minFilter : textureMinFilter.LINEAR;
    this.setMagFilter(gl, magFilter);
    this.setMinFilter(gl, minFilter);
  }
}

class DepthStencilTexture extends Texture {
  constructor(gl, params) {
    super(gl, params);
  }

  _initFormats(gl) {
    this.immutable = false;

    this.internalFormat = GL.checkWebGL2() ? gl.DEPTH24_STENCIL8 : gl.DEPTH_STENCIL;
    this.dataType = GL.checkWebGL2() ? gl.UNSIGNED_INT_24_8 : extensions.get("WEBGL_depth_texture").UNSIGNED_INT_24_8_WEBGL;
    this.dataFormat = gl.DEPTH_STENCIL;
  }

  _setupStates(gl) {
    this.setMagFilter(gl, textureMagFilter.NEAREST);
    this.setMinFilter(gl, textureMinFilter.NEAREST);
  }
}

class ColorFloatRenderingTexture extends Texture {
  constructor(gl, params) {
    super(gl, params);
  }

  _initFormats(gl) {
    this.immutable = false;

    extensions.get("OES_texture_half_float_linear");
    if (GL.checkWebGL2()) {
      const ext = extensions.get("EXT_color_buffer_half_float");
      this.internalFormat = ext.RGBA16F_EXT;
      this.dataType = gl.HALF_FLOAT;
    } else {
      const ext = extensions.get("OES_texture_half_float");
      this.internalFormat = gl.RGBA;
      this.dataType = ext.HALF_FLOAT_OES;
    }
    this.dataFormat = gl.RGBA;
  }

  _setupStates(gl) {
    this.setMagFilter(gl, textureMagFilter.NEAREST);
    this.setMinFilter(gl, textureMinFilter.NEAREST);
  }
}

class ColorRG32FTexture extends Texture {
  constructor(gl, params) {
    super(gl, params);
  }

  _initFormats(gl) {
    this.immutable = true;

    this.internalFormat = gl.RG32F;
    this.dataFormat = gl.RG;
    this.dataType = gl.FLOAT;
  }

  _setupStates(gl) {
    this.setMagFilter(gl, textureMagFilter.NEAREST);
    this.setMinFilter(gl, textureMinFilter.NEAREST);
  }
}

class ColorRGBACompressedTexture extends Texture {
  constructor(gl, params) {
    super(gl, params);
  }

  _initFormats(gl, params) {
    this.immutable = true;
    this.internalFormat = glTypes.getCompressedTextureInternalFormat(params.format);
  }

  _setupStates(gl, params) {
    const magFilter = params.magFilter !== undefined ? params.magFilter : textureMagFilter.LINEAR;
    const minFilter = params.minFilter !== undefined ? params.minFilter : textureMinFilter.LINEAR;
    this.setMagFilter(gl, magFilter);
    this.setMinFilter(gl, minFilter);
  }

  _preInitStorage() {}

  initMipMapLevelImage() {
    output.error(output.DEVELOPERS_ERROR, "Mip map level initialization from image is not supported for compressed texture.");
  }

  initMipMapLevelBuffer(level, buffer) {
    const gl = GL.getContext();
    gl.bindTexture(this.target, this.pixelBuffer);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, this.flipY);
    gl.compressedTexImage2D(this.target, level, this.internalFormat, this.width, this.height, 0, buffer);
    gl.bindTexture(this.target, null);
  }
}

class CubeMap extends Texture {
  constructor(gl, params) {
    super(gl, params);
  }

  _initFormats(gl) {
    this.immutable = true;
    this.target = gl.TEXTURE_CUBE_MAP;

    if (gl.texStorage2D) {
      this.internalFormat = gl.RGBA8;
    } else {
      this.internalFormat = gl.RGBA;
    }

    this.dataFormat = gl.RGBA;
    this.dataType = gl.UNSIGNED_BYTE;
  }

  _preInitStorage(gl) {
    if (gl.texStorage2D) {
      gl.texStorage2D(this.target, this.levels, this.internalFormat, this.width, this.height);
    } else {
      for (const target of [gl.TEXTURE_CUBE_MAP_POSITIVE_X, gl.TEXTURE_CUBE_MAP_NEGATIVE_X, gl.TEXTURE_CUBE_MAP_POSITIVE_Y, gl.TEXTURE_CUBE_MAP_NEGATIVE_Y, gl.TEXTURE_CUBE_MAP_POSITIVE_Z, gl.TEXTURE_CUBE_MAP_NEGATIVE_Z]) {
        for (let level=0; level<this.levels; level++) {
          gl.texImage2D(target, level, this.internalFormat, this.width / Math.pow(2, level), this.height / Math.pow(2, level), 0, this.dataFormat, this.dataType, null);
        }
      }
    }
  }

  _setupStates(gl, params) {
    const magFilter = params.magFilter !== undefined ? params.magFilter : textureMagFilter.LINEAR;
    const minFilter = params.minFilter !== undefined ? params.minFilter : textureMinFilter.LINEAR;
    this.setMagFilter(gl, magFilter);
    this.setMinFilter(gl, minFilter);
  }
}

class Renderbuffer {

  constructor(gl, params) {

    this.isTexture = false;

    const width = params.width ? params.width : 1;
    const height = params.height ? params.height : 1;

    const maxSamples = extensions.maxSamplesCount();
    this.samples = params.samples ? params.samples : 1;
    this.samples = Math.min(this.samples, maxSamples);

    this.width = 1;
    this.height = 1;

    this.pixelBuffer =  gl.createRenderbuffer();

    this.format = params.format != undefined ? params.format : RBIntFormat.R5G6B5;

    this.resize(width, height);

  }

  resize(w, h) {

    const gl = GL.getContext();

    const {width, height} = fixBufferSize(w, h);

    gl.bindRenderbuffer(gl.RENDERBUFFER, this.pixelBuffer);

    const format = glTypes.getRBInternalFormat(this.format);

    if (this.samples == 1) {
      gl.renderbufferStorage(gl.RENDERBUFFER, format, width, height);
    } else {
      gl.renderbufferStorageMultisample(gl.RENDERBUFFER, this.samples, format,
                                        width, height);
    }

    gl.bindRenderbuffer(gl.RENDERBUFFER, null);

    this.width = width;
    this.height = height;

    memoryProfiler.markMemoryAllocated(this.pixelBuffer, memoryType.GPU_TEXTURES, width * height * glTypes.getRBElementSize(this.format, this.samples));

  }

  cleanup() {
    const gl = GL.getContext();
    gl.deleteRenderbuffer(this.pixelBuffer);
    memoryProfiler.markMemoryReleased(this.pixelBuffer);
  }

}

function fixTextureSize(width, height) {

  width = Math.max(width, 1);
  height = Math.max(height, 1);

  const gl = GL.getContext();
  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);

  if (width > maxTextureSize || height > maxTextureSize) {

    const newWidth  = Math.min(width, maxTextureSize);
    const newHeight = Math.min(height, maxTextureSize);

    output.warn("Requested texture size \"" + width + "x" + height + "\" is too large." +
                "Reducing the size to \"" + newWidth + "x" + newHeight + "\"");

    width = newWidth;
    height = newHeight;

  }

  return {width, height}
}

function fixBufferSize(width, height) {

  width = Math.max(width, 1);
  height = Math.max(height, 1);

  const gl = GL.getContext();
  const maxRbSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE);

  if (width > maxRbSize || height > maxRbSize) {

    const newWidth  = Math.min(width, maxRbSize);
    const newHeight = Math.min(height, maxRbSize);

    output.warn("Requested RenderBuffer size \"" + width + "x" + height + "\" is too large." +
                "Reducing the size to \"" + newWidth + "x" + newHeight + "\"");

    width = newWidth;
    height = newHeight;

  }

  return {width, height}
}
